<template>
  <rest-list-page
    :page-title="pageTitle"
    :fetch-endpoint="fetchEndpoint"
    :v-if="columnsReady"
    :breadcrumbs="breadcrumbs"
    ref="profilesList"
    :empty-text="emptyText"
    :no-filters-empty-text="noFiltersEmptyText"
  >
    <template slot="actions">
      <default-view-settings
        :settings="defaultViewSettings"
        :current-state="selectedColumns"
        v-if="displayDefaultViewSettings"
      />
      <router-link :to="createRoute" v-if="!readonly">
        <el-button type="primary" icon="el-icon-plus">
          {{ $t('UserProfiles_ProfileList_Create') }}
        </el-button>
      </router-link>

      <router-link :to="importRoute" v-if="!readonly">
        <el-button type="primary" icon="el-icon-upload">
          {{ $t('UserProfiles_ProfileList_Import') }}
        </el-button>
      </router-link>
    </template>

    <template slot="table" v-if="columnsReady">
      <rest-list-column
        prop="id"
        fixed
        :title="$t('UserProfiles_Field_Id')"
        :min-width="100"
        :filter-on-enter="filterOnEnterEnabled"
      >
        <template slot-scope="{ row }">
          <div class="id-cell">
            <el-tooltip effect="light" placement="top" :content="row.id">
              <router-link :to="rowClickRoute(row)" class="user-link">
                <icon glyph="user-profile" class="user-profile-icon" />
              </router-link>
            </el-tooltip>
          </div>
        </template>
      </rest-list-column>
      <template>
        <div :key="listUpdateKey">
          <div v-for="selectedColumn in Object.keys(selectedColumns)">
            <rest-list-column
              :key="selectedColumn"
              v-if="selectedColumns.num && selectedColumn === 'num'"
              prop="num"
              :title="$t('UserProfiles_Field_Num')"
              :width="200"
              :filter-on-enter="filterOnEnterEnabled"
            />

            <rest-list-column-enum
              :key="selectedColumn"
              v-if="
                selectedColumns.identifier && selectedColumn === 'identifier'
              "
              prop="identifier"
              :enum="profileIdentifiersLabels"
              :title="$t('UserProfiles_Field_Identifier')"
              :min-width="120"
            />

            <rest-list-column
              :key="selectedColumn"
              v-if="
                selectedColumns.customer_id &&
                  selectedColumn === 'customer_id' &&
                  $store.state.app.features.customerIdEnabled
              "
              prop="customer_id"
              :title="$t('UserProfiles_Field_CustomerId')"
              :min-width="180"
              :filter-on-enter="filterOnEnterEnabled"
            />
            <rest-list-column
              :key="selectedColumn"
              v-if="selectedColumns.user_id && selectedColumn === 'user_id'"
              prop="user_id"
              :title="$t('UserProfiles_Field_UserId')"
              :min-width="180"
              :filter-on-enter="filterOnEnterEnabled"
            />

            <rest-list-column
              :key="selectedColumn"
              v-if="selectedColumns.email && selectedColumn === 'email'"
              prop="email"
              :title="$t('UserProfiles_Field_Email')"
              :min-width="180"
              :filter-on-enter="filterOnEnterEnabled"
              :min-chars="4"
              :debounce-time="500"
            />

            <rest-list-column
              :key="selectedColumn"
              v-if="
                selectedColumns.mobile_number &&
                  selectedColumn === 'mobile_number'
              "
              prop="mobile_number"
              :title="$t('UserProfiles_Field_MobileNumber')"
              :min-width="180"
              :filter-on-enter="filterOnEnterEnabled"
              :min-chars="6"
              :debounce-time="500"
            />

            <rest-list-column
              :key="selectedColumn"
              v-if="
                selectedColumns.whatsapp_number &&
                  selectedColumn === 'whatsapp_number'
              "
              prop="whatsapp_number"
              :title="$t('UserProfiles_Field_WhatsappNumber')"
              :min-width="180"
              :filter-on-enter="filterOnEnterEnabled"
              :min-chars="6"
              :debounce-time="500"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.email_addressable &&
                  selectedColumn === 'email_addressable'
              "
              prop="email_addressable"
              :title="$t('UserProfiles_Field_EmailAddressable')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.email_subscription &&
                  selectedColumn === 'email_subscription'
              "
              prop="email_subscription"
              :title="$t('UserProfiles_Field_EmailSubscription')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.sms_addressable &&
                  selectedColumn === 'sms_addressable'
              "
              prop="sms_addressable"
              :title="$t('UserProfiles_Field_SmsAddressable')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.sms_subscription &&
                  selectedColumn === 'sms_subscription'
              "
              prop="sms_subscription"
              :title="$t('UserProfiles_Field_SmsSubscription')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.whatsapp_addressable &&
                  selectedColumn === 'whatsapp_addressable'
              "
              prop="whatsapp_addressable"
              :title="$t('UserProfiles_Field_WhatsappAddressable')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="
                selectedColumns.whatsapp_subscription &&
                  selectedColumn === 'whatsapp_subscription'
              "
              prop="whatsapp_subscription"
              :title="$t('UserProfiles_Field_WhatsappSubscription')"
              :min-width="140"
            />

            <rest-list-column-boolean
              :key="selectedColumn"
              v-if="selectedColumns.archived && selectedColumn === 'archived'"
              prop="merge_to"
              :title="$t('UserProfiles_Field_Archived')"
              :min-width="140"
            />

            <template v-if="isExtraColumnSelected(selectedColumn)">
              <rest-list-column-enum
                v-if="isColumnPreference(selectedColumn)"
                :key="selectedColumn"
                :prop="getPropName(selectedColumn)"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :number="false"
                :sortable="false"
                :enum="preferenceFilterOptions"
              />
              <rest-list-column-date
                v-else-if="
                  hasAttributeType(selectedColumn, TagType.DATETIME) ||
                    hasAttributeType(selectedColumn, TagType.DATE) ||
                    hasAttributeType(selectedColumn, TagType.UNENFORCED_DATE)
                "
                :key="selectedColumn"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :sortable="false"
                :unix="false"
                :is-range="false"
                :date-format="getDateFormat(selectedColumn)"
                :date-picker-props="getDatePickerOptions(selectedColumn)"
                :prop="getPropName(selectedColumn)"
              />
              <rest-list-column
                v-else-if="hasAttributeType(selectedColumn, TagType.BOOLEAN)"
                :key="selectedColumn"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :sortable="false"
                :filter-input="true"
                :filter-options="booleanAttributeOptions"
                :prop="getPropName(selectedColumn)"
              />
              <rest-list-column
                v-else-if="hasAttributeType(selectedColumn, TagType.STRING)"
                :key="selectedColumn"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :sortable="false"
                :filter-input="true"
                :prop="getPropName(selectedColumn)"
                :filter-on-enter="filterOnEnterEnabled"
              />
              <rest-list-column-number
                v-else-if="
                  hasAttributeType(selectedColumn, TagType.NUMBER) ||
                    hasAttributeType(selectedColumn, TagType.INTEGER) ||
                    hasAttributeType(selectedColumn, TagType.UNENFORCED_NUMBER)
                "
                :key="selectedColumn"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :sortable="false"
                :formatter="numberFormatter"
                :filter-number="true"
                :prop="getPropName(selectedColumn)"
                :filter-on-enter="filterOnEnterEnabled"
              />
              <rest-list-column
                v-else-if="isColumnAttribute"
                :key="selectedColumn"
                :title="extraColumns[selectedColumn].title"
                :min-width="180"
                :sortable="false"
                :filter-input="false"
                :prop="getPropName(selectedColumn)"
                :filter-on-enter="filterOnEnterEnabled"
              />
            </template>
          </div>
        </div>
      </template>

      <el-table-column
        :width="canStoreColumnsState ? 50 : -1"
        align="center"
        fixed="right"
      >
        <template slot="header" slot-scope="{}">
          <rest-list-column-settings
            storage-key="profiles-list-columns"
            :predefined-columns="predefinedColumns"
            :state-overriding-allowed="canStoreColumnsState"
            :title="$t('Profiles_Field_Settings')"
            :columns="columns"
            :labeled="isAdvancedSearchAvailable"
            :disabled-columns="!dataLoaded"
            :select-limit="20"
            v-model="selectedColumns"
            sortable
            filterable
          />
        </template>
        <template slot-scope="scope">
          <template v-if="checkSeedListIsAvailable || checkActivityAuditLink">
            <div class="actions">
              <el-dropdown trigger="click" @command="handleActions">
                <div class="dropdown-btn-wrapper">
                  <el-icon name="more"></el-icon>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item
                      v-if="checkSeedListIsAvailable"
                      :command="{
                        type: 'toggleSeedList',
                        profile: scope.row
                      }"
                      :icon="
                        scope.row.in_seed_list
                          ? 'el-icon-close'
                          : 'el-icon-plus'
                      "
                    >
                      {{
                        scope.row.in_seed_list
                          ? $t('UserProfiles_ProfileList_RemoveFromSeedList')
                          : $t('UserProfiles_ProfileList_AddToSeedList')
                      }}
                    </el-dropdown-item>
                    <el-dropdown-item v-if="checkActivityAuditLink">
                      <el-link
                        :href="workflowActivityAuditLink(scope.row)"
                        icon="el-icon-data-line"
                        :underline="false"
                        target="_blank"
                      >
                        {{ $t('Profiles_Button_WorkflowActivityAudit') }}
                      </el-link>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </div>
              </el-dropdown>
            </div>
          </template>
        </template>
      </el-table-column>
    </template>
  </rest-list-page>
</template>

<script>
import RestListPage from '@/common/components/rest/RestListPageNew'
import RestListColumn from '@/common/components/rest/RestListColumn'
import RestListColumnEnum from '@/common/components/rest/RestListColumnEnum'
import RestListColumnBoolean from '@/common/components/rest/RestListColumnBoolean'
import RestListColumnSettings from '@/common/components/rest/RestListColumnSettings'
import RestListColumnDate from '@/common/components/rest/RestListColumnDate'
import RestListColumnNumber from '@/common/components/rest/RestListColumnNumber'
import { projectEndpoint, projectRoute, customConfirm } from '@/core/helpers'
import { profileIdentifiersLabels } from '@/modules/profiles/enum/ProfileIdentifiersEnum'
import DefaultViewSettings from '@/modules/profiles/components/DefaultViewSettings'
import { $isSuperAdmin, $hasFeature, $can } from '@/core/utils/VueAcl'
import RestApi from '@/core/services/RestApi'
import { TagType } from '@/modules/data-manager/enum/TagTypeEnum'
import {
  subscriptionStatus,
  subscriptionStatusKeys
} from '@/modules/profiles/enum/SubscriptionStatusEnum'

export default {
  name: 'ProfileListPage',

  components: {
    RestListColumnBoolean,
    RestListColumnEnum,
    RestListColumn,
    RestListPage,
    RestListColumnSettings,
    RestListColumnDate,
    RestListColumnNumber,
    DefaultViewSettings
  },

  data() {
    return {
      listUpdateKey: 0,
      expandColumns: '',
      selectedColumns: {},
      customAttributes: [],
      preferencesFormatted: [],
      preferencesWithChannel: {},
      preferenceFilterOptions: {},
      columnsReady: false,
      profileIdentifiersLabels,
      mxaDomain: this.$store.getters.mxaDomain,
      TagType,
      subscriptionStatus,
      subscriptionStatusKeys
    }
  },

  mounted() {
    if (this.isAdvancedSearchAvailable) {
      this.loadAdvancedSearchAttributes()
    } else {
      this.columnsReady = true
    }
  },

  watch: {
    selectedColumns: {
      deep: true,
      handler() {
        this.setExpandColumns()
        this.listUpdateKey++
      }
    }
  },
  computed: {
    pageTitle() {
      return this.$t(this.$route.meta.title)
    },

    breadcrumbs() {
      return [
        {
          label: this.$t('Users_Breadcrumb_Data')
        },
        {
          label: this.$t('Users_Breadcrumb_Profiles')
        }
      ]
    },

    fetchEndpoint() {
      let params = {
        merge_to: ''
      }

      if (this.checkSeedListIsAvailable) {
        params.expand = 'in_seed_list'
      }

      if (this.expandColumns) {
        if (params.expand) {
          params.expand += ',' + this.expandColumns
        } else {
          params.expand = this.expandColumns
        }
      }

      return projectEndpoint({
        method: 'get',
        url: `user-profiles`,
        params: params
      })
    },

    fetchAttributesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `tags`,
        params: { attribute_profile: 1, sort: 'column_name', limit: 500 }
      })
    },

    fetchPreferencesEndpoint() {
      return projectEndpoint({
        method: 'get',
        url: `consent-preferences`,
        params: { limit: 500 }
      })
    },

    createRoute() {
      return projectRoute({
        name: 'ProfileCreate'
      })
    },

    importRoute() {
      return projectRoute({
        name: 'ImportCreate'
      })
    },

    readonly() {
      return !this.$can('Profile::Update')
    },

    features() {
      return this.$store.state.app.features
    },

    userRole() {
      return this.$store.state.app.user.role
    },

    filterOnEnterEnabled() {
      return this.userRole === 'csa'
    },

    emptyText() {
      if (!['csa', 'consent-customer-service-agent'].includes(this.userRole)) {
        return null
      }

      return this.$t('UserProfiles_Filters_NotFound')
    },

    noFiltersEmptyText() {
      if (!['csa', 'consent-customer-service-agent'].includes(this.userRole)) {
        return null
      }

      return this.$t('UserProfiles_Filters_Suggestion')
    },

    columns() {
      return {
        num: {
          title: this.$t('UserProfiles_Field_Num'),
          defaultValue: false,
          validation: true
        },
        customer_id: {
          title: this.$t('UserProfiles_Field_CustomerId'),
          defaultValue: false,
          validation: this.$store.state.app.features.customerIdEnabled
        },
        identifier: {
          title: this.$t('UserProfiles_Field_Identifier'),
          defaultValue: false,
          validation: true
        },
        user_id: {
          title: this.$t('UserProfiles_Field_UserId'),
          defaultValue: true,
          validation: this.$can('UserPersonalData::Read')
        },
        email: {
          title: this.$t('UserProfiles_Field_Email'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        mobile_number: {
          title: this.$t('UserProfiles_Field_MobileNumber'),
          defaultValue: false,
          validation: this.$can('UserPersonalData::Read')
        },
        whatsapp_number: {
          title: this.$t('UserProfiles_Field_WhatsappNumber'),
          defaultValue: false,
          validation:
            this.features.whatsapp && this.$can('UserPersonalData::Read')
        },
        email_addressable: {
          title: this.$t('UserProfiles_Field_EmailAddressable'),
          defaultValue: false,
          validation: true
        },
        email_subscription: {
          title: this.$t('UserProfiles_Field_EmailSubscription'),
          defaultValue: false,
          validation: true
        },
        sms_addressable: {
          title: this.$t('UserProfiles_Field_SmsAddressable'),
          defaultValue: false,
          validation: true
        },
        sms_subscription: {
          title: this.$t('UserProfiles_Field_SmsSubscription'),
          defaultValue: false,
          validation: true
        },
        whatsapp_addressable: {
          title: this.$t('UserProfiles_Field_WhatsappAddressable'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        whatsapp_subscription: {
          title: this.$t('UserProfiles_Field_WhatsappSubscription'),
          defaultValue: false,
          validation: this.features.whatsapp
        },
        archived: {
          title: this.$t('UserProfiles_Field_Archived'),
          defaultValue: false,
          validation: true
        },

        ...this.extraColumns
      }
    },

    checkActivityAuditLink() {
      return $hasFeature('maxautomation') && $isSuperAdmin()
    },

    checkSeedListIsAvailable() {
      return (
        this.$can('manageProjectTechnical') &&
        $hasFeature('featureSeedList') &&
        $hasFeature('seedListEnabled')
      )
    },

    isAdvancedSearchAvailable() {
      return $can('canUseAdvancedSearch') && this.isAdvancedSearchEnabled
    },

    isAdvancedSearchEnabled() {
      return $hasFeature('advancedProfileSearch')
    },

    extraColumns() {
      if (!this.isAdvancedSearchAvailable) {
        return {}
      }

      return {
        ...this.customAttributes.reduce((result, item) => {
          let key = item.column_name + ' ' // prevent numeric keys to break sort
          result[key] = {
            key: item.name,
            title: item.column_name,
            defaultValue: false,
            validation: true,
            label: 'RestForm_Settings_Attribute'
          }
          return result
        }, {}),
        ...this.preferencesFormatted.reduce((result, item) => {
          let key = item.name + ' ' // prevent numeric keys to break sort
          result[key] = {
            key: item.name,
            title: item.name,
            defaultValue: false,
            validation: true,
            label: 'RestForm_Settings_Preference'
          }
          return result
        }, {})
      }
    },

    booleanAttributeOptions() {
      return {
        0: 0,
        1: 1
      }
    },

    isConsentPreferencesEnabled() {
      return $hasFeature('subscriptionPreferences')
    },

    dataLoaded() {
      return !this.$refs.profilesList.$refs.list['isLoading']
    },

    defaultViewSettings() {
      return this.$store.state.app.project.default_profile_view
    },

    displayDefaultViewSettings() {
      return (
        this.defaultViewSettings.users > 0 &&
        (this.$isRole('admin') ||
          this.$isRole('admin-support') ||
          this.$isRole('provider-admin') ||
          this.$isRole('company-admin') ||
          this.$isRole('project-admin'))
      )
    },

    canStoreColumnsState() {
      return !(
        this.$isRole('consent-customer-service-agent') &&
        (this.defaultViewSettings.schema.change_restrict || false)
      )
    },

    predefinedColumns() {
      if (
        !this.$isRole('consent-customer-service-agent') ||
        !this.defaultViewSettings.defined
      ) {
        return {}
      }

      return this.defaultViewSettings.schema.columns || {}
    }
  },

  methods: {
    rowClickRoute(row) {
      return projectRoute({
        name: 'ProfileOverview',
        query: {
          profile_id: row.id
        }
      })
    },

    rowDeleteEndpoint(row) {
      return projectEndpoint({
        method: 'delete',
        url: `user-profiles/${row.id}`
      })
    },

    rowToggleSeedListEndpoint(row) {
      if (row.in_seed_list) {
        return projectEndpoint({
          method: 'delete',
          url: `seed-list/${row.id}`
        })
      }

      return projectEndpoint({
        method: 'post',
        url: `seed-list`,
        data: {
          profile_id: row.id
        }
      })
    },

    toggleSeedList(row) {
      RestApi(this.rowToggleSeedListEndpoint(row)).then(() => {
        this.$refs.profilesList.refresh()
      })
    },

    workflowActivityAuditLink(row) {
      return `https://${this.mxaDomain}/project/${row.project_id}/admin/profiles/${row.id}/activity`
    },

    handleActions(command) {
      switch (command.type) {
        case 'toggleSeedList':
          let message = command.profile.in_seed_list
            ? 'UserProfiles_SeedList_ConfirmRemove'
            : 'UserProfiles_SeedList_ConfirmAdd'
          customConfirm(
            '',
            this.$t(message),
            this.$t('UserProfiles_Text_Yes'),
            this.$t('UserProfiles_Text_No')
          )
            .then(() => {
              RestApi(this.rowToggleSeedListEndpoint(command.profile)).then(
                () => {
                  this.$refs.profilesList.refresh()
                }
              )
            })
            .catch(() => {})
          break
      }
    },

    loadAdvancedSearchAttributes() {
      let requestsPool = [
        RestApi({
          ...this.fetchAttributesEndpoint
        })
      ]
      if (this.isConsentPreferencesEnabled) {
        requestsPool.push(
          RestApi({
            ...this.fetchPreferencesEndpoint
          })
        )
      }

      Promise.all(requestsPool).then(values => {
        this.customAttributes = values[0].data
        if (this.isConsentPreferencesEnabled) {
          values[1].data.forEach(channel => {
            if (
              channel.hasOwnProperty('preferences') &&
              channel.preferences.length > 0
            ) {
              channel.preferences.forEach(preference => {
                let name = preference.name
                if (preference.channel) {
                  name = preference.channel + '.' + name
                }
                this.preferencesFormatted.push({
                  name
                })
                this.preferencesWithChannel[name] = {
                  preference: preference.name,
                  channel: preference.channel
                }
              })
            }
          })
        }
        this.setExpandColumns()
        this.setPreferenceFilterOptions()

        this.columnsReady = true
      })
    },

    isExtraColumnSelected(column) {
      if (this.extraColumns[column] === undefined) {
        return false
      }

      return this.selectedColumns[column]
    },

    isColumnPreference(column) {
      return (
        this.isExtraColumnSelected(column) &&
        this.extraColumns[column].label &&
        this.extraColumns[column].label === 'RestForm_Settings_Preference'
      )
    },

    isColumnAttribute(column) {
      return (
        this.isExtraColumnSelected(column) &&
        this.extraColumns[column].label &&
        this.extraColumns[column].label === 'RestForm_Settings_Attribute'
      )
    },

    hasAttributeType(column, type) {
      if (!this.isColumnAttribute(column)) {
        return false
      }

      let filtered = [...this.customAttributes].filter(item => {
        return (
          item.name === column.trim() || item.display_name === column.trim()
        )
      })

      if (filtered.length > 0) {
        return filtered[0].type === type
      }

      return false
    },

    getTrimmedColumnName(column) {
      return column.trim()
    },

    getPropName(column) {
      const columnKey = this.extraColumns[column].key
      const trimmedColumnKey = this.getTrimmedColumnName(columnKey)
      if (this.isColumnPreference(column)) {
        let channelData = this.preferencesWithChannel[trimmedColumnKey]
        let fieldName = channelData.preference
        if (channelData.channel.length > 0) {
          fieldName =
            'chnl:' + channelData.channel + '.' + channelData.preference
        }

        return 'preferences[' + fieldName + ']'
      }

      return 'attributes[' + trimmedColumnKey + ']'
    },

    getDateFormat(column) {
      if (this.hasAttributeType(column, TagType.DATETIME)) {
        return 'YYYY-MM-DD HH:mm:ss'
      }

      return 'YYYY-MM-DD'
    },

    getDatePickerOptions(column) {
      let options = {
        type: 'date',
        value_format: 'yyyy-MM-dd',
        defaultTime: null
      }

      if (this.hasAttributeType(column, TagType.DATETIME)) {
        options.type = 'datetime'
        options.value_format = 'yyyy-MM-dd HH:mm:ss'
      }

      return options
    },

    setExpandColumns() {
      let expand = []
      Object.keys(this.extraColumns).forEach(key => {
        let trimmedKey = this.extraColumns[key].key.trim() // trim the keys which have spaces
        if (this.selectedColumns[key] || this.selectedColumns[trimmedKey]) {
          let type = 'attributes'
          let fieldName = trimmedKey
          if (this.extraColumns[key].label === 'RestForm_Settings_Preference') {
            type = 'preferences'
            let channelData = this.preferencesWithChannel[trimmedKey]
            if (channelData.channel.length > 0) {
              fieldName =
                'chnl:' + channelData.channel + '.' + channelData.preference
            } else {
              fieldName = channelData.preference
            }
          }
          type += '[' + fieldName + ']'
          expand.push(type)
        }
      })

      this.expandColumns = expand.sort().join(',')
    },

    numberFormatter(value) {
      if (value === null || value === undefined) {
        return '-'
      }

      return value
    },

    setPreferenceFilterOptions() {
      Object.assign(this.preferenceFilterOptions, this.subscriptionStatus)
      this.preferenceFilterOptions[this.subscriptionStatusKeys.Undefined] =
        'Profiles_Channels_StatusNotProvided'
    }
  }
}
</script>

<style scoped>
.columnSelection {
  min-width: 300px;
  display: flex;
  flex-direction: column;
  padding-left: 5px;
}
.actions .el-icon-more {
  cursor: pointer;
  transform: rotate(90deg);
}
.el-dropdown-menu__item:not(.is-disabled):hover,
.el-dropdown-menu__item:focus {
  background-color: #fff;
}
.user-link {
  text-decoration: none !important;
}
.user-profile-icon {
  font-size: large;
  top: 3px;
  position: relative;
  color: #1d87c9;
}
.user-profile-icon:hover {
  color: #176ca1;
}
.id-cell {
  text-align: center;
}
</style>



// WEBPACK FOOTER //
// src/modules/profiles/pages/ProfileListPage.vue